import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from '_common/hooks';
import { Select, SelectProps, SelectOption, Paragraph } from '@ets/design-system';

import { useGetElementStatusListQuery } from '_common/services/api/elementStatusApi';
import { selectUserIsAdmin } from '_common/services/api/authority';
import { addTag, removeTag } from 'App/redux/appSlice';
import {
  useGetTagsListQuery,
  useCreateTagMutation,
} from 'Settings/pages/TenantSettingsPage/Tags/TagsApi';

import { Tag } from '@ets/design-system';

type TagSystemProps = {
  objectId: ObjectId;
  testId: string;
  placeholder: string;
  size?: 'm' | 'l';
} & Pick<SelectProps<SelectOption>, 'menuPlacement' | 'width'>;

type Option = {
  label: string;
  value: string;
};

const TagSystem = ({
  objectId,
  testId,
  placeholder,
  menuPlacement,
  size = 'm',
  width = '100%',
}: TagSystemProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const object = useSelector((state: RootState) => state.app.data[objectId]);
  const objectTags: string[] = object.tags;
  const userIsAdmin = useSelector(selectUserIsAdmin);

  const [mutCreateTag] = useCreateTagMutation();
  const { status } = useGetElementStatusListQuery(undefined, {
    selectFromResult: ({ data }) => ({ status: data?.entities[object.status] }),
  });

  const { data: tags, isLoading } = useGetTagsListQuery();

  const handleAddTag: SelectProps<Option>['onChange'] = (option) => {
    dispatch(addTag({ objectId: object.id, objectType: object.type, tag: option?.value ?? '' }));
  };

  const handleRemoveTag = (value: Tag) => {
    dispatch(removeTag({ objectId: object.id, objectType: object.type, tag: value }));
  };

  const handleCreateTag = (tag: Tag) => {
    mutCreateTag(tag);
    if (!object.tags.includes(tag)) {
      dispatch(addTag({ objectId: object.id, objectType: object.type, tag: tag ?? '' }));
    }
  };

  const checkPermissions = () => {
    return (
      object.user_permissions.includes('owner') ||
      object.user_permissions.includes('edit') ||
      object.user_permissions.includes('admin')
    );
  };
  const hasEditPermission = checkPermissions();

  return (
    <div
      className="flex max-h-full min-w-0 flex-1 flex-col"
      data-testid={`${testId}-tags-container`}
    >
      {hasEditPermission && (
        <div className="mb-4 flex min-w-0 flex-1 flex-col">
          <Select
            variant="standard"
            testId={testId}
            disabled={status && !status?.['allow_edit']}
            isCreatable={userIsAdmin}
            isSearchable
            size={size}
            width={width}
            value={null}
            onChange={handleAddTag}
            options={
              object.tags &&
              tags?.list
                .slice()
                .sort()
                .filter((tag) => !object.tags.includes(tag))
                .map((tag) => ({ value: tag, label: tag }))
            }
            placeholder={placeholder}
            noOptionsMessage={({ inputValue }) => {
              if (tags?.list.length === 0) {
                return intl.formatMessage({ id: 'TENANT_WITHOUT_TAGS' });
              }
              if (tags?.list.length === object.tags.length) {
                return intl.formatMessage({ id: 'ALL_TAGS_IN_USE' });
              }
              return intl.formatMessage({ id: 'NO_TAGS_FOUNDED' }, { tag: inputValue });
            }}
            onCreateOption={handleCreateTag}
            escapeClearsValue
            isValidNewOption={(input) => input.length > 0}
            isLoading={isLoading}
            menuPlacement={menuPlacement}
          />
        </div>
      )}

      {object.tags.length === 0 && (
        <Paragraph className="text-neutral-50" size="s">
          <FormattedMessage id="NO_TAGS_ASSOCIATED_WITH_THIS_DOCUMENT" />
        </Paragraph>
      )}

      {object.tags.length > 0 && (
        <div className="flex flex-wrap">
          {objectTags.map((tag) => (
            <Tag
              key={tag}
              label={tag}
              variant="primary"
              size="s"
              onClick={
                status?.allow_edit && checkPermissions() ? () => handleRemoveTag(tag) : undefined
              }
              style={{ margin: '0 1rem 1rem 0' }}
              testId={`${testId}-${tag}`}
            ></Tag>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagSystem;
